import { action, computed, flow, makeObservable, observable } from 'mobx';
import { GlobalStore } from '@roc/feature-app-core';
import { DocumentStore } from '@roc/feature-documents';
import { LoanStore } from '@roc/feature-loans';
import { Document } from '@roc/feature-types';
import { DocumentName } from '../../../constants';
import { shouldBeAbleToSubmitSpecificForm } from '../../../utils/documentForms';
import { TaskStatus, TaskStatusDB } from '@roc/feature-general-tasks';
import { TitleAgentV2Service } from '../../../services/documentForms/titleAgentV2Service';
import { TitleCompanyFormStore } from './titleCompanyFormStore';

export const COMMUNICATE_VIA_EMAIL = 'Email';
export const COMMUNICATE_VIA_LOAN_CHAT = 'LoanChat';

export const COMMUNICATE_VIA_OPTIONS = [
  { label: 'Email', value: COMMUNICATE_VIA_EMAIL },
  { label: 'Loan Chat', value: COMMUNICATE_VIA_LOAN_CHAT },
];

export interface TitleContact {
  contactId?: number;
  fullName: string;
  role?: string;
  phone?: string;
  email?: string;
}

export interface TitleCompanySelection {
  titleAgencyId: number | null;
  companyId: number | null;
  name: string;
  phone?: string;
  email?: string;
  streetNumber?: string;
  streetName?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  address?: string;
  contacts: TitleContact[];
}

const formatAddress = (raw: any): string => {
  if (!raw) return '';
  if (raw.address) return raw.address;
  if (raw.fullAddress) return raw.fullAddress;
  const line = [raw.streetNumber, raw.streetName].filter(Boolean).join(' ');
  const cityState = [raw.city, raw.state].filter(Boolean).join(', ');
  return [line, cityState, raw.zipCode].filter(Boolean).join(' ').trim();
};

const toContact = (c: any): TitleContact => ({
  contactId: c?.contactId,
  fullName: c?.fullName ?? [c?.firstName, c?.lastName].filter(Boolean).join(' '),
  role: c?.roleInEntityCurrent ?? c?.role ?? '',
  phone: c?.phone ?? c?.cellPhone ?? '',
  email: c?.email ?? c?.emailAddress ?? '',
});

const toSelection = (raw: any): TitleCompanySelection => ({
  titleAgencyId: raw?.titleAgencyId ?? null,
  companyId: raw?.companyId ?? raw?.titleAgencyCompanyId ?? null,
  name: raw?.name ?? '',
  phone: raw?.phone ?? '',
  email: raw?.email ?? '',
  streetNumber: raw?.streetNumber ?? '',
  streetName: raw?.streetName ?? '',
  city: raw?.city ?? '',
  state: raw?.state ?? '',
  zipCode: raw?.zipCode ?? '',
  address: formatAddress(raw),
  contacts: Array.isArray(raw?.contacts) ? raw.contacts.map(toContact) : [],
});

interface FormSnapshot {
  titleAgencyId: number | null;
  communicateVia: string;
  selectedContactIds: string;
  address: string;
  streetNumber: string;
  streetName: string;
  city: string;
  state: string;
  zipCode: string;
}

export class TitleAgentV2Store {
  loanStore: LoanStore;
  documentStore: DocumentStore;
  globalStore: GlobalStore;
  titleAgentService: TitleAgentV2Service;
  titleCompanyFormStore: TitleCompanyFormStore;

  /** Raw autocomplete options (mapped selections) fetched for the lender. */
  titleCompanies: TitleCompanySelection[] = [];
  searchText = '';
  selectedCompany: TitleCompanySelection | null = null;
  showCreateForm = false;
  communicateVia = COMMUNICATE_VIA_EMAIL;
  loading = false;
  isExistingCompanySelected = false;
  pendingExistingCompany: TitleCompanySelection | null = null;
  contacts: TitleContact[] = [];
  selectedContactIds: Set<number> = new Set();
  taskStatus: string | null = null;
  initialSnapshot: FormSnapshot | null = null;
  originalCompany: TitleCompanySelection | null = null;

  constructor(
    loanStore: LoanStore,
    documentStore: DocumentStore,
    globalStore: GlobalStore
  ) {
    this.loanStore = loanStore;
    this.documentStore = documentStore;
    this.globalStore = globalStore;
    this.titleAgentService = new TitleAgentV2Service();
    this.titleCompanyFormStore = new TitleCompanyFormStore(globalStore);
    makeObservable(this, {
      titleCompanies: observable,
      searchText: observable,
      selectedCompany: observable,
      showCreateForm: observable,
      communicateVia: observable,
      loading: observable,
      isExistingCompanySelected: observable,
      pendingExistingCompany: observable,
      contacts: observable,
      selectedContactIds: observable,
      taskStatus: observable,
      initialSnapshot: observable,
      originalCompany: observable,
      initForm: flow,
      fetchTitleCompanies: flow,
      submit: flow,
      addAndSelectNewContact: action,
      setSearchText: action,
      selectExistingCompany: action,
      openCreateForm: action,
      cancelCreateForm: action,
      createCompanyFromForm: action,
      changeCompany: action,
      setCommunicateVia: action,
      toggleContact: action,
      updateContact: action,
      takeSnapshot: action,
      reset: action,
      canSubmit: computed,
      isAccepted: computed,
      hasChanged: computed,
      isCompanySwitched: computed,
    });
  }

  reset() {
    this.titleCompanies = [];
    this.searchText = '';
    this.selectedCompany = null;
    this.showCreateForm = false;
    this.communicateVia = COMMUNICATE_VIA_EMAIL;
    this.loading = false;
    this.isExistingCompanySelected = false;
    this.pendingExistingCompany = null;
    this.contacts = [];
    this.selectedContactIds = new Set();
    this.taskStatus = null;
    this.initialSnapshot = null;
    this.originalCompany = null;
    this.titleCompanyFormStore.reset();
  }

  private getLenderId(): number {
    return (
      this.loanStore.loanDetails?.lenderId ??
      this.globalStore.lenderInfo?.lenderId
    );
  }

  *initForm(loanId: string, taskStatus?: string) {
    this.reset();
    this.taskStatus = taskStatus ?? null;
    yield this.fetchTitleCompanies();
    const current = this.loanStore.loanDetails?.titleCompany;
    if (current?.companyId || current?.titleAgencyId) {
      // Prefer titleCompanies (fetched from new_portal with full ContactBO including contactId)
      // over loanDetails.titleCompany whose contacts lack contactId.
      const fullCompany = this.titleCompanies.find(c =>
        (current.titleAgencyId && c.titleAgencyId === current.titleAgencyId) ||
        (current.companyId && c.companyId === current.companyId)
      );
      this.selectExistingCompany(fullCompany ?? toSelection(current));
    }
    this.communicateVia = COMMUNICATE_VIA_EMAIL;
    const savedIds: number[] = this.loanStore.loanDetails?.selectedTitleContactIds ?? [];
    this.selectedContactIds = new Set(savedIds);
    this.takeSnapshot();
  }

  *fetchTitleCompanies() {
    const lenderId = this.getLenderId();
    if (!lenderId) {
      this.titleCompanies = [];
      return;
    }
    this.loading = true;
    try {
      const response = yield this.titleAgentService.getTitleCompaniesByLender(
        lenderId
      );
      const raw = response?.data?.data;
      const list = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : [];
      this.titleCompanies = (Array.isArray(list) ? list : []).map(toSelection);
    } catch (error) {
      this.titleCompanies = [];
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while loading title companies',
      });
    } finally {
      this.loading = false;
    }
  }

  setSearchText(text: string) {
    const next = text ?? '';
    if (this.isExistingCompanySelected && next !== this.searchText) {
      this.isExistingCompanySelected = false;
      this.pendingExistingCompany = null;
      this.titleCompanyFormStore.reset();
    }
    this.searchText = next;
    if (!this.isExistingCompanySelected) {
      this.titleCompanyFormStore.onFieldChange('name', next.trim());
    }
  }

  selectExistingCompany(company: TitleCompanySelection | null) {
    if (!company) {
      this.changeCompany();
      return;
    }
    this.titleCompanyFormStore.reset();
    this.titleCompanyFormStore.onFieldChange('name', company.name ?? '');
    this.titleCompanyFormStore.onFieldChange('address', company.address ?? '');
    this.titleCompanyFormStore.onFieldChange('streetNumber', company.streetNumber ?? '');
    this.titleCompanyFormStore.onFieldChange('streetName', company.streetName ?? '');
    this.titleCompanyFormStore.onFieldChange('city', company.city ?? '');
    this.titleCompanyFormStore.onFieldChange('state', company.state ?? '');
    this.titleCompanyFormStore.onFieldChange('zipCode', company.zipCode ?? '');
    if (company.contacts.length > 0) {
      this.contacts = company.contacts.map((c, i) => ({
        ...c,
        contactId: c.contactId ?? -(i + 1),
        phone: c.phone || (i === 0 ? company.phone ?? '' : ''),
        email: c.email || (i === 0 ? company.email ?? '' : ''),
      }));
    } else if (company.email || company.phone) {
      this.contacts = [{
        contactId: -1,
        fullName: '',
        phone: company.phone ?? '',
        email: company.email ?? '',
      }];
    } else {
      this.contacts = [];
    }
    // Restore saved selection when re-picking the same company; clear for a new company.
    const isSameAsOriginal =
      this.initialSnapshot != null &&
      (company.titleAgencyId ?? null) === this.initialSnapshot.titleAgencyId;
    if (isSameAsOriginal && this.initialSnapshot) {
      const savedIds: number[] = JSON.parse(this.initialSnapshot.selectedContactIds);
      this.selectedContactIds = new Set(savedIds);
    } else {
      this.selectedContactIds = new Set();
    }
    this.pendingExistingCompany = company;
    this.isExistingCompanySelected = true;
    this.searchText = company.name;
  }

  /** Pre-fill the create form name from the autocomplete "Add X" option. */
  openCreateForm(name: string) {
    this.isExistingCompanySelected = false;
    this.pendingExistingCompany = null;
    this.contacts = [];
    this.selectedContactIds = new Set();
    this.titleCompanyFormStore.reset();
    if (name) {
      this.titleCompanyFormStore.onFieldChange('name', name);
    }
  }

  cancelCreateForm() {
    this.titleCompanyFormStore.reset();
  }

  /** Confirm the company from the unified form — existing or newly created. */
  createCompanyFromForm() {
    if (this.isExistingCompanySelected && this.pendingExistingCompany) {
      this.selectedCompany = this.pendingExistingCompany;
      this.isExistingCompanySelected = false;
      this.pendingExistingCompany = null;
      this.showCreateForm = false;
      this.titleCompanyFormStore.reset();
      return;
    }
    this.titleCompanyFormStore.runFormValidationWithMessage();
    if (!this.titleCompanyFormStore.form.meta.isValid) {
      return;
    }
    const v = this.titleCompanyFormStore.getFormValues();
    const contact: TitleContact = {
      fullName: v.contactName,
      role: v.contactRole,
      phone: v.contactPhone,
      email: v.contactEmail,
    };
    this.selectedCompany = {
      titleAgencyId: null,
      companyId: null,
      name: v.name,
      phone: v.contactPhone,
      email: v.contactEmail,
      streetNumber: v.streetNumber,
      streetName: v.streetName,
      city: v.city,
      state: v.state,
      zipCode: v.zipCode,
      address: v.address,
      contacts: contact.fullName ? [contact] : [],
    };
    this.showCreateForm = false;
    this.titleCompanyFormStore.reset();
  }

  changeCompany() {
    if (this.isAccepted && this.originalCompany) {
      // X-clear during an accepted task edit: restore the company that was loaded when the form opened.
      this.selectExistingCompany(this.originalCompany);
      if (this.initialSnapshot) {
        this.communicateVia = COMMUNICATE_VIA_EMAIL;
        const savedIds: number[] = JSON.parse(this.initialSnapshot.selectedContactIds);
        this.selectedContactIds = new Set(savedIds);
      }
      return;
    }
    this.selectedCompany = null;
    this.showCreateForm = false;
    this.isExistingCompanySelected = false;
    this.pendingExistingCompany = null;
    this.contacts = [];
    this.selectedContactIds = new Set();
    this.searchText = '';
    this.titleCompanyFormStore.reset();
  }

  setCommunicateVia(value: string) {
    this.communicateVia = value;
  }

  toggleContact(contactId: number) {
    const next = new Set(this.selectedContactIds);
    if (next.has(contactId)) {
      next.delete(contactId);
    } else {
      next.add(contactId);
    }
    this.selectedContactIds = next;
  }

  updateContact(contactId: number, updated: TitleContact) {
    this.contacts = this.contacts.map(c =>
      c.contactId === contactId ? { ...c, ...updated, contactId } : c
    );
  }

  addAndSelectNewContact(draft: TitleContact) {
    const tempId = -(this.contacts.length + 1);
    const newContact: TitleContact = { ...draft, contactId: tempId };
    this.contacts = [...this.contacts, newContact];
    this.selectedContactIds = new Set([...this.selectedContactIds, tempId]);
  }

  takeSnapshot() {
    const v = this.titleCompanyFormStore.getFormValues();
    const company = this.pendingExistingCompany;
    this.initialSnapshot = {
      titleAgencyId: company?.titleAgencyId ?? null,
      communicateVia: this.communicateVia,
      selectedContactIds: JSON.stringify([...this.selectedContactIds].sort((a, b) => a - b)),
      address: v.address || company?.address || '',
      streetNumber: v.streetNumber || company?.streetNumber || '',
      streetName: v.streetName || company?.streetName || '',
      city: v.city || company?.city || '',
      state: v.state || company?.state || '',
      zipCode: v.zipCode || company?.zipCode || '',
    };
    this.originalCompany = company ?? null;
  }

  get hasChanged() {
    if (!this.initialSnapshot) return true;
    const s = this.initialSnapshot;
    const v = this.titleCompanyFormStore.getFormValues();
    const company = this.pendingExistingCompany;
    const currentIds = JSON.stringify([...this.selectedContactIds].sort((a, b) => a - b));
    return (
      (company?.titleAgencyId ?? null) !== s.titleAgencyId ||
      currentIds !== s.selectedContactIds ||
      (v.address || company?.address || '') !== s.address ||
      (v.streetNumber || company?.streetNumber || '') !== s.streetNumber ||
      (v.streetName || company?.streetName || '') !== s.streetName ||
      (v.city || company?.city || '') !== s.city ||
      (v.state || company?.state || '') !== s.state ||
      (v.zipCode || company?.zipCode || '') !== s.zipCode
    );
  }

  get isCompanySwitched() {
    if (!this.initialSnapshot) return false;
    return (this.pendingExistingCompany?.titleAgencyId ?? null) !== this.initialSnapshot.titleAgencyId;
  }

  get canEdit() {
    const entitlements = this.globalStore.userFeatures?.closingEntitlements;
    return shouldBeAbleToSubmitSpecificForm(
      entitlements?.isSubmissionAllowed,
      entitlements?.isSubmissionNotAllowedOnly,
      entitlements?.isSubmissionAllowedOnly,
      DocumentName.TITLE_AGENT
    );
  }

  get isAccepted() {
    return this.taskStatus === TaskStatusDB.ACCEPTED || this.taskStatus === TaskStatus.ACCEPTED;
  }

  get canSubmit() {
    if (!this.canEdit) return false;
    if (this.isAccepted) {
      return this.selectedContactIds.size > 0 && this.hasChanged;
    }
    return !!this.searchText.trim() && this.selectedContactIds.size > 0;
  }

  *submit(document: Document, loanId: string) {
    if (!this.canSubmit) {
      return;
    }
    try {
      const v = this.titleCompanyFormStore.getFormValues();
      const existing = this.isExistingCompanySelected ? this.pendingExistingCompany : null;
      const selectedContacts = this.contacts.filter(
        c => c.contactId != null && this.selectedContactIds.has(c.contactId)
      );
      const firstContact = selectedContacts[0] ?? this.contacts[0];
      const titleAgentFormDetails = {
        titleAgencyId: existing?.titleAgencyId ?? null,
        titleCompanyId: existing?.companyId ?? null,
        name: this.searchText.trim(),
        cellPhone: firstContact?.phone ?? '',
        email: firstContact?.email ?? '',
        contactName: firstContact?.fullName ?? '',
        contacts: this.contacts.map(c => ({
          ...c,
          contactId: c.contactId != null && c.contactId > 0 ? c.contactId : undefined,
        })),
        selectedTitleContactIds: Array.from(this.selectedContactIds).filter(id => id > 0),
        address: v.address || existing?.address,
        streetName: v.streetName || existing?.streetName || '',
        streetNumber: v.streetNumber || existing?.streetNumber || '',
        city: v.city || existing?.city || '',
        state: v.state || existing?.state || '',
        zipCode: v.zipCode || existing?.zipCode || '',
        communicateVia: this.communicateVia,
        isWimba: false,
      };
      const loanDocument = this.isAccepted ? { ...document } : { ...document, status: TaskStatusDB.PENDING };

      yield this.titleAgentService.updateTitleAgentForm({
        loanDocument,
        loanId: parseInt(loanId),
        titleAgentFormDetails,
      });
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Title agent form saved successfully',
      });
      yield this.loanStore.fetchLoanDetails(document.loanId);
      this.documentStore.documentFormStore.setCurrentForm(null);
      this.loanStore.refreshPage();
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while submitting title agent form',
      });
    }
  }
}

export default TitleAgentV2Store;
